import { computeSerializedOffsetAt } from './serialized-text-walk';

/**
 * Convert a DOM Range inside an editable block into character offsets in
 * the block's SERIALIZED text — the form stored in Redux / DB, where
 * existing chips appear as `{{ref:UUID}}` tokens and styled spans render
 * as <b>…</b>, <i>…</i>, etc.
 *
 * Why we can't use range.startOffset directly: it's a position inside a
 * single text node. Once the block contains an existing chip or any
 * inline styling, the text-node offset diverges from the serialized-text
 * offset that insertTokenAtPosition / replaceRangeWithToken operate on.
 *
 * Why we can't cloneContents + serializeHtmlToText either: cloned content
 * is always balanced HTML, so a range ending inside a <b> would over-
 * count the closing tag. This walker stops at the endpoint and counts
 * only the chars emitted up to that point — including the opening tag of
 * any partially-entered inline element.
 *
 * Returns null when either end of the range escapes the block.
 */
export function computeSerializedOffsets(
  blockEl: HTMLElement,
  range: Range,
): { position: number; length: number } | null {
  if (!blockEl.contains(range.startContainer)) return null;
  if (!blockEl.contains(range.endContainer)) return null;

  const position = computeSerializedOffsetAt(
    blockEl,
    range.startContainer,
    range.startOffset,
  );
  if (position === null) return null;

  const endPos = computeSerializedOffsetAt(
    blockEl,
    range.endContainer,
    range.endOffset,
  );
  if (endPos === null) return null;

  return { position, length: endPos - position };
}
